import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { Editor } from '@tiptap/core';
import { CustomImage, Indent, InlineField, NumberedHeading, Subtitle, Summary } from '@app/pages/documents/documents-dynamic-templates/editor-extensions';
import Underline from '@tiptap/extension-underline';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Table from '@tiptap/extension-table';
import Highlight from '@tiptap/extension-highlight';
import { PaginationPlus } from 'tiptap-pagination-plus';
import { PaginationTable } from 'tiptap-table-plus';
import StarterKit from '@tiptap/starter-kit';
import { Node } from '@tiptap/pm/model';

import { DynamicDocumentService } from '@app/services';
import { DynamicTemplateThemeEnum, FieldsValuesBySigner, InlineFieldContext } from '@app/models';
import { interval, takeWhile, tap } from 'rxjs';

const { TablePlus, TableCellPlus, TableRowPlus, TableHeaderPlus } = PaginationTable;

@Component({
  selector: 'app-dynamic-document-viewer',
  templateUrl: './dynamic-document-viewer.component.html',
  styleUrl: './dynamic-document-viewer.component.scss',
  standalone: false
})
export class DynamicDocumentViewerComponent implements OnInit, OnChanges {
  @Input() content: string;
  @Input() theme: DynamicTemplateThemeEnum;
  @Input() alreadyFilledFields: FieldsValuesBySigner[];
  @Input() scale = 1;
  @Input() context: InlineFieldContext;

  @Output() recalculateScale = new EventEmitter();

  isMobile: boolean;
  editor: Editor;

  readonly DynamicTemplateThemeEnum = DynamicTemplateThemeEnum;

  @ViewChild('editor', { static: false }) editorRef!: ElementRef<HTMLElement>;

  constructor(private dynamicDocumentService: DynamicDocumentService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (this.editor) {
      if (changes['content'] && changes['content'].currentValue) {
        this.editor.destroy();
        this.initializeEditorView();
      }

      if (changes['alreadyFilledFields'] && changes['alreadyFilledFields'].currentValue) {
        this.replaceInlineFieldsWithValues();
      }
    }

    interval(100)
      .pipe(
        tap(() =>
          setTimeout(() => {
            this.fixImagesView();
            (document.querySelector('div#editor > div.tiptap') as HTMLDivElement)?.style.removeProperty('min-height');
          })
        ),
        takeWhile(value => value < 5)
      )
      .subscribe();
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;
    setTimeout(() => this.initializeEditorView(), 200);
  }

  initializeEditorView() {
    this.content = this.dynamicDocumentService.removeOnlyNumberFieldsFromHTML(this.content);

    const tablePlusExtension = !/<table[^>]*>[\s\S]*?<\/table>/g.test(this.content)
      ? []
      : [
          TablePlus.configure({
            resizeHandleStyle: {
              display: 'none'
            }
          })
        ];
    this.editor = new Editor({
      element: this.editorRef.nativeElement,
      extensions: [
        NumberedHeading.configure({
          levels: [1, 2, 3, 4]
        }),
        Subtitle,
        Underline,
        Superscript,
        Subscript,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'subtitle']
        }),
        TextStyle,
        Color,
        Indent,
        Highlight.configure({
          multicolor: true
        }),
        InlineField.configure({
          context: this.context
        }),
        TableRowPlus,
        TableCellPlus,
        TableHeaderPlus,
        ...tablePlusExtension,
        Table.configure({
          resizable: true
        }),
        CustomImage.configure({
          allowBase64: true
        }),
        PaginationPlus.configure({
          pageHeight: 1123,
          pageGap: 16,
          pageGapBorderSize: 1,
          pageBreakBackground: 'var(--clr-structure-bg-50)',
          pageHeaderHeight: 94,
          pageFooterHeight: 94,
          footerRight: '{page}',
          footerLeft: '',
          headerRight: '',
          headerLeft: '',
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          contentMarginTop: 0,
          contentMarginBottom: 0
        }),
        StarterKit.configure({
          heading: false
        }),
        Summary.configure({
          getSummaryContent: (() => this.dynamicDocumentService.getDynamicDocumentSummaryHTML(this.editor)).bind(this)
        })
      ],
      content: this.content || '',
      editable: false
    });

    setTimeout(() => {
      this.editor.commands.focus();
      window.scrollTo({ top: 0 });

      this.fixImagesView();
      this.recalculateScale.emit();

      this.replaceInlineFieldsWithValues();
      this.editor.commands.forceUpdateSummary();
    });
  }

  replaceInlineFieldsWithValues() {
    this.alreadyFilledFields?.forEach(({ signer, fields }) => {
      this.fillDocumentFieldsWithValues(fields, signer);
    });
  }

  fillDocumentFieldsWithValues(result: Record<string, string>, signer: number = 0) {
    Object.keys(result).forEach((key: string) => {
      const { state } = this.editor;
      const { doc, tr } = state;

      const inlineFieldsToFill: Array<{ node: Node; pos: number }> = new Array();
      doc.descendants((node, pos) => {
        if (node.type.name !== 'inlineField') return true;
        if (node.attrs['name'] !== key) return true;
        if (node.attrs['signer'] !== signer) return true;

        inlineFieldsToFill.push({ node, pos });
      });

      inlineFieldsToFill.reverse().forEach(({ node, pos }) => {
        let textNode = state.schema.text(result[key] || ' ');

        this.editor.commands.setTextSelection({ from: pos, to: pos + 1 });

        const marks: Record<string, boolean> = {
          bold: this.editor.isActive('bold'),
          italic: this.editor.isActive('italic'),
          strike: this.editor.isActive('strike'),
          underline: this.editor.isActive('underline')
        };

        const activeMarks: Array<string> = Object.keys(marks)
          .map(mark => (!!marks[mark] ? mark : null))
          .filter(mark => mark !== null);

        for (const mark of activeMarks) {
          const markType = state.schema.marks[mark];
          if (markType) {
            textNode = textNode.mark([...textNode.marks, markType.create()]);
          }
        }

        tr.replaceWith(pos, pos + node.nodeSize, textNode);
        return true;
      });

      if (tr.docChanged) {
        this.editor.view.dispatch(tr);
      }
    });
  }

  fixImagesView() {
    this.editorRef.nativeElement.querySelectorAll('.image-plus-container').forEach((container: HTMLElement) => {
      if (!!container.getAttribute('alignment')) return;

      const wrapper = document.createElement('div');
      wrapper.classList.add('image-plus-wrapper');

      wrapper.style.display = 'flex';
      wrapper.style.width = '100%';

      container.style.cursor = 'text';
      container.setAttribute('alignment', container.firstElementChild.getAttribute('alignment'));

      container.parentElement.insertBefore(wrapper, container);
      wrapper.appendChild(container);
    });
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMobile = window.innerWidth <= 992;
  }
}
